import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from './AuthProvider';
import { colors } from '../theme/designTheme';

const AUTH_ENABLED = process.env.REACT_APP_AUTH_ENABLED !== 'false';

/**
 * Route guard — redirects unauthenticated users to /login.
 *
 * Props:
 *   requiredRole  — optional role gate (e.g. "Admin"). If the user
 *                   doesn't have this role, shows an access-denied message.
 */
const RequireAuth = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();

  // Dev mode — allow everything
  if (!AUTH_ENABLED) return children;

  // MSAL still processing redirect — show nothing until done
  if (isLoading) return null;

  // Not authenticated — redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role check (optional)
  if (requiredRole && !hasRole(requiredRole, 'Admin')) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: colors.bg,
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Lexend',
            fontWeight: 700,
            fontSize: '18px',
            color: colors.error,
          }}
        >
          Access Denied
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lexend',
            fontSize: '13px',
            color: colors.textSecondary,
          }}
        >
          You need the <strong>{requiredRole}</strong> role to access this page.
        </Typography>
      </Box>
    );
  }

  return children;
};

export default RequireAuth;
